import React from 'react';
import Icon from '../../../components/AppIcon';
import { Card, CardContent } from '../../../components/ui/Card';

const BankInformationTab = ({ vendor }) => {
  // Helper function to format account type
  const formatAccountType = (accountType) => {
    if (!accountType) return 'N/A';
    const accountTypeMap = {
      'savings': 'Savings Account',
      'current': 'Current Account',
      'od': 'Overdraft Account'
    };
    return accountTypeMap[accountType] || accountType;
  };
  // Use real vendor bank data instead of static data
  const bankAccounts = vendor?.bank_info ? [vendor.bank_info] : [];

  // If no bank info in relationship, try to use main vendor fields
  if (bankAccounts.length === 0 && vendor?.bank_name) {
    bankAccounts.push({
      id: 1,
      bank_name: vendor.bank_name,
      branch_name: vendor.branch_name,
      account_number: vendor.account_number,
      ifsc_code: vendor.ifsc_code,
      swift_code: vendor.swift_code,
      account_type: vendor.account_type,
      bank_address: vendor.bank_address,
      isPrimary: true,
      verificationDate: null
    });
  }

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'verified':
        return 'bg-emerald-100 text-emerald-800';
      case 'pending verification':
      case 'pending':
        return 'bg-amber-100 text-amber-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-slate-100 text-slate-800';
    }
  };

  return (
    <div className="space-y-5">
      {/* Bank Accounts */}
      {bankAccounts.length > 0 ? (
        <div className="space-y-4">
          {bankAccounts.map((account, index) => (
            <Card key={account.id || index} className="border border-slate-200 shadow-sm bg-white">
              <CardContent className="p-6">
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center">
                      <Icon name="CreditCard" size={24} className="text-slate-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-slate-900 flex items-center space-x-2">
                        <span>{account.bank_name || 'N/A'}</span>
                        {account.isPrimary && (
                          <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full font-medium">
                            Primary
                          </span>
                        )}
                      </h3>
                      <p className="text-sm text-slate-600">{account.branch_name || 'N/A'}</p>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-600">Account Holder Name</label>
                    <p className="text-sm text-slate-900 font-medium">{vendor?.company_name || 'N/A'}</p>
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-600">Account Number</label>
                    <p className="text-sm text-slate-900 font-mono">{account.account_number || 'N/A'}</p>
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-600">
                      {vendor?.country_origin === 'IN' ? 'IFSC Code' : 'Swift Code'}
                    </label>
                    <p className="text-sm text-slate-900 font-mono">
                      {vendor?.country_origin === 'IN' ? (account.ifsc_code || 'N/A') : (account.swift_code || 'N/A')}
                    </p>
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-600">Account Type</label>
                    <p className="text-sm text-slate-900">{formatAccountType(account.account_type)}</p>
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-600">Currency</label>
                    <p className="text-sm text-slate-900">{vendor?.preferred_currency || 'INR'}</p>
                  </div>
                </div>

                {/* Bank Address */}
                {account.bank_address && (
                  <div className="mt-4 pt-4 border-t border-slate-200">
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-slate-600">Bank Address</label>
                      <p className="text-sm text-slate-900">{account.bank_address}</p>
                    </div>
                  </div>
                )}

              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="border border-slate-200 shadow-sm bg-white">
          <CardContent className="p-6">
            <div className="text-center py-12">
              <Icon name="CreditCard" size={64} className="text-slate-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-900 mb-2">No Bank Information Available</h3>
              <p className="text-slate-600 mb-6">This vendor hasn't provided bank information yet.</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Payment History Summary - Empty for new vendors */}
      <Card className="border border-slate-200 shadow-sm bg-white">
        <CardContent className="p-6">
          <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <Icon name="TrendingUp" size={20} className="text-slate-600" />
            Payment History Summary
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="text-2xl font-semibold text-slate-900 mb-1">₹0</div>
              <div className="text-sm text-slate-600">Total Payments Received</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-semibold text-slate-900 mb-1">0</div>
              <div className="text-sm text-slate-600">Total Transactions</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-semibold text-slate-900 mb-1">N/A</div>
              <div className="text-sm text-slate-600">Average Payment Time</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Recent Transactions - Empty for new vendors */}
      <Card className="border border-slate-200 shadow-sm bg-white">
        <CardContent className="p-6">
          <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <Icon name="History" size={20} className="text-slate-600" />
            Recent Transactions
          </h3>
          <div className="text-center py-8">
            <Icon name="History" size={48} className="text-slate-400 mx-auto mb-4" />
            <p className="text-slate-600">No transaction history available</p>
            <p className="text-sm text-slate-600 mt-1">Transactions will appear here once the vendor starts receiving payments</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default BankInformationTab;